<template>
  <ConfirmModal wrapper-class="p-6 w-11/12" frame-class="cursor-default text-main" @close-dialog="closePopup">
    <template #title>
      <h1 class="text-headline-m" data-testid="prospects-row-popup-title">
        {{ t('ProspectsCatalog.popupHeadline') }}
      </h1>
    </template>

    <template #content>
      <AlertBox v-if="!prospect.available" type="info" class="mt-5" data-testid="prospects-popup-unavailable-alert">
        <template #header>
          {{ t('ProspectsCatalog.prospectNotAvailableTitle') }}
        </template>
        {{ t('ProspectsCatalog.prospectNotAvailableDescription') }}
      </AlertBox>
      <TableLight class="mt-6 w-full" data-testid="prospects-popup-detail-table">
        <thead>
          <tr>
            <th class="text-left">{{ t('ProspectsCatalog.visitedEvents') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companyName') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.position') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companyDepartment') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companyJobLevel') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companyField') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companyIndustry') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companySize') }}</th>
            <th class="text-left">{{ t('ProspectsCatalog.companyCountry') }}</th>
          </tr>
        </thead>
        <tbody>
          <ProspectRowPopupRow
            v-for="(company, i) in props.prospect.companies"
            :key="`${props.prospect.id}-company-${i}`"
            :company="company"
            :facet-items="props.facetItems"
            :prospect="props.prospect"
          />
        </tbody>
      </TableLight>
    </template>

    <template #actions>
      <div class="mt-8 flex justify-end">
        <button class="btn-link-color-m" data-testid="prospects-popup-close" @click.stop="closePopup">{{ t('close') }}</button>
        <DropDownSelect v-if="canAdd" inner-class="w-fit" direction="right" position="bottom" data-testid="prospects-popup-add-dropdown">
          <template #activator="{ open }">
            <button class="btn-primary-color-s ml-4" data-testid="prospects-popup-add-trigger" @click.stop="open">
              <SvgIcon :path="mdiPlaylistPlus" class="mr-3 text-inverse" />
              {{ t('ProspectsCatalog.addToList') }}
            </button>
          </template>

          <template #default="{ close }">
            <ul class="m-0 list-none p-0" data-testid="prospects-popup-add-options">
              <template v-if="prospectLists.length">
                <li v-for="list in props.prospectLists" :key="`list_${list.id}`" class="separate-rows">
                  <label
                    class="block cursor-pointer px-3 py-2 peer-checked:bg-primary-subtler hover:bg-primary-subtler"
                    @click.stop="addProspectToList(list.id, close)"
                  >
                    {{ list.title }}
                  </label>
                </li>
              </template>
              <template v-else>
                <li class="separate-rows">
                  <label class="block cursor-pointer px-3 py-2 peer-checked:bg-primary-subtler hover:bg-primary-subtler">
                    {{ $t('ProspectsCatalog.addToListNoListFound') }}
                  </label>
                </li>
              </template>
            </ul>
          </template>
        </DropDownSelect>
        <button
          v-if="props.prospectListId && isChangeable"
          class="btn-primary-color-s ml-4"
          data-testid="prospects-popup-remove"
          @click.stop="deleteProspectItem"
        >
          <SvgIcon :path="mdiMinusCircleOutline" class="mr-3 text-inverse" />
          {{ t('ProspectsCatalog.removeFromList') }}
        </button>
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { mdiMinusCircleOutline, mdiPlaylistPlus } from '@mdi/js'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { ConfirmModal } from '@/components'
import type { ProspectFacetItems } from '@/components/Prospects/prospectFacetItems'
import { TableLight } from '@/ui/TableLight'

import { findActiveList, isListChangeable } from './helpers'
import ProspectRowPopupRow from './ProspectRowPopupRow.vue'
import type { ProspectItem, ProspectList } from './types'

const props = defineProps<{
  facetItems: ProspectFacetItems
  prospect: ProspectItem
  prospectListId?: string
  prospectLists: ProspectList[]
  disableAdd?: boolean
}>()

const { t } = useI18n()

const canAdd = computed(() => !props.prospectListId && props.prospect.available && !props.disableAdd)

const emit = defineEmits<{
  (e: 'createProspectItem', value: { prospectId: string; prospectListId: string }): void
  (e: 'deleteProspectItem', value: { prospectId: string; prospectListId: string }): void
  (e: 'hideDetails'): void
}>()

const activeList = findActiveList(props.prospectLists, props.prospectListId)
const isChangeable = computed(() => isListChangeable(activeList.value))

function closePopup() {
  emit('hideDetails')
}

function addProspectToList(prospectListId: string, callback: () => void) {
  emit('createProspectItem', { prospectId: props.prospect.id, prospectListId: prospectListId })
  callback()
  emit('hideDetails')
}

function deleteProspectItem() {
  if (props.prospectListId) {
    emit('deleteProspectItem', { prospectId: props.prospect.id, prospectListId: props.prospectListId })
  }
  emit('hideDetails')
}
</script>
